/**
 * Onboarding (1.6.7, D-1.6.7-5): the one function that decides whether a person is onboarded into a program.
 *
 * The rule (Elijah, 7 Oct 2026): a new person is onboarded into every program they join, waiver or not.
 *   - A program with an agreement — Housekeeping (the release), Companion & Wellness (the tenant waiver), Tech
 *     Support (the Tech Support agreement): the agreement is part of onboarding and booking waits for it.
 *   - A program with no agreement (Reading & Services today): onboarding is the sign-up itself — the program
 *     ticked, contact details, the program's own questions — and booking waits for that.
 *   - Existing tenants are not re-gated: a person with a visit in the program (not cancelled) whose service day is
 *     before ONBOARDING_FROM_DAY is `complete` with `existing: true`, whatever is missing. The missing pieces are
 *     still listed, so the profile can show them; nothing is blocked by them.
 *
 * Pure: no list is read here. The server gathers the facts (api/src/people/onboarding.ts) and the pages word what
 * this answers (the piece keys are stable; the screen's words are the i18n keys `onb167.piece.<key>`).
 *
 * What is enforced where (logs/1.6.7-decisions-o.md): Tech Support, the volunteer request and the Companion /
 * Reading booking are refused by the server for a new person who is not onboarded. Housekeeping's gate is the
 * scheduler's own (Queue 1's eligibility) — for Housekeeping this function only drives what the Book sheet and
 * the profile say.
 */

/** The first service day of this version (yyyyMMdd): a visit before it is history from before the rule. */
export const ONBOARDING_FROM_DAY = '20261008';

export const ONB_PROGRAMS = ['housekeeping', 'companion', 'reading', 'tech'] as const;
export type OnbProgram = (typeof ONB_PROGRAMS)[number];

/** ProgramCodeSnapshot on SM_Appointments for each program. */
export const ONB_PROGRAM_CODE: Record<OnbProgram, string> = { housekeeping: 'HOUSEKEEPING', companion: 'COMPANION', reading: 'READING', tech: 'TECH' };

export const ONB_PIECES = ['enrolled', 'contact', 'housekeeping-release', 'home-visit', 'companion-waiver', 'mobility-aid', 'visions-answers', 'tech-agreement'] as const;
export type OnbPiece = (typeof ONB_PIECES)[number];

/**
 * The pieces of each program's onboarding, in the order they are asked. Reading & Services asks no questions of
 * its own today; when it does, they are added here. Tech Support has no switch on the card (its enrollment is made
 * by the first booking), so it has no `enrolled` piece: everybody signs the agreement.
 */
export const PROGRAM_PIECES: Record<OnbProgram, readonly OnbPiece[]> = {
  housekeeping: ['enrolled', 'contact', 'housekeeping-release', 'home-visit'],
  companion: ['enrolled', 'contact', 'companion-waiver', 'mobility-aid', 'visions-answers'],
  reading: ['enrolled', 'contact'],
  tech: ['contact', 'tech-agreement'],
};

/** The agreement of each program that has one. */
export const PROGRAM_AGREEMENT: Record<OnbProgram, OnbPiece | null> = { housekeeping: 'housekeeping-release', companion: 'companion-waiver', reading: null, tech: 'tech-agreement' };

/** A visit that counts as history: anything that was not cancelled. */
export const isHistoryStatus = (appointmentStatus: string): boolean => appointmentStatus !== 'Cancelled';

export interface OnboardingFacts {
  /** The program is ticked: an enrollment that has not ended and was not turned down. */
  enrolled?: boolean;
  /** A phone number or an email address on the record. */
  contact?: boolean;
  release?: boolean;
  homeVisit?: boolean;
  /** 1.8.1 (D-1.8.1-3): the administrator pressed Skip home visit — staff: the piece is done by override (who). */
  homeVisitSkippedBy?: string | null;
  waiver?: boolean;
  /** D-1026-EMERGENCY: kept for the record only — never a piece of onboarding. */
  emergency?: boolean;
  mobility?: boolean;
  visions?: boolean;
  /** null: the agreements list is not on the site yet — nothing can be recorded, so nothing is asked (1.5.3). */
  techAgreement?: boolean | null;
  /** The service days (yyyyMMdd) of the person's visits in this program that were not cancelled. */
  visitDays?: readonly string[];
}

const FACT_OF: Record<OnbPiece, (f: OnboardingFacts) => boolean | null> = {
  enrolled: (f) => !!f.enrolled,
  contact: (f) => !!f.contact,
  'housekeeping-release': (f) => !!f.release,
  'home-visit': (f) => !!f.homeVisit || !!f.homeVisitSkippedBy,
  'companion-waiver': (f) => !!f.waiver,
  'mobility-aid': (f) => !!f.mobility,
  'visions-answers': (f) => !!f.visions,
  'tech-agreement': (f) => (f.techAgreement === null ? null : !!f.techAgreement),
};

export interface Onboarding {
  program: OnbProgram;
  /** Booking in the program may go on: every piece is in, or the person is an existing tenant of the program. */
  complete: boolean;
  /** On the site with a visit in the program before this version: not re-gated. */
  existing: boolean;
  done: number;
  total: number;
  /** The pieces still missing, in the order they are asked (listed for an existing tenant too). */
  missing: OnbPiece[];
}

/** One program on a person's record: `joined` says the profile shows a line for it. */
export interface ProgramOnboarding extends Onboarding {
  joined: boolean;
  /**
   * 1.6.8 (D-1.6.8-4): the person has started in the program, so what is missing is worth saying on their card.
   * The same as `joined` for a program with a switch. Tech Support has none and most tenants never use it: started
   * is an enrollment or a visit (not cancelled) in Tech Support. Absent on an answer from before 1.6.8.
   */
  started?: boolean;
}

export type OnboardingByProgram = Record<OnbProgram, ProgramOnboarding>;

/** GET /api/onboarding?tenantId= — null for a staff unit or an office (not a person joining a program). */
export interface OnboardingResponse {
  tenantId: string;
  name: string;
  unit: string;
  onboarding: OnboardingByProgram | null;
}

export const hasHistoryBefore = (visitDays: readonly string[] | undefined, fromDay: string = ONBOARDING_FROM_DAY): boolean => (visitDays ?? []).some((d) => /^\d{8}$/.test(d) && d < fromDay);

/** The decision. */
export function onboardingOf(program: OnbProgram, facts: OnboardingFacts, fromDay: string = ONBOARDING_FROM_DAY): Onboarding {
  const asked = PROGRAM_PIECES[program].map((key) => ({ key, done: FACT_OF[key](facts) })).filter((p): p is { key: OnbPiece; done: boolean } => p.done !== null);
  const missing = asked.filter((p) => !p.done).map((p) => p.key);
  const existing = hasHistoryBefore(facts.visitDays, fromDay);
  return { program, complete: existing || missing.length === 0, existing, done: asked.length - missing.length, total: asked.length, missing };
}

/** May a visit in the program be booked for them, as far as onboarding goes? (the first missing piece when not) */
export function onboardingBlock(o: Onboarding | null | undefined): OnbPiece | null {
  return o && !o.complete ? (o.missing[0] ?? null) : null;
}

/* ------------------------------------------------------------------------------------------------
 * Words (English — the server's refusals and the audit; the screen says the same through i18n)
 * ---------------------------------------------------------------------------------------------- */

export const PROGRAM_NAME_EN: Record<OnbProgram, string> = { housekeeping: 'Housekeeping', companion: 'Companion & Wellness', reading: 'Reading & Services', tech: 'Tech Support' };

export const PIECE_EN: Record<OnbPiece, string> = {
  enrolled: 'the program itself (it is not turned on for them)',
  contact: 'a phone number or an email address',
  'housekeeping-release': 'the housekeeping release',
  'home-visit': 'the home visit',
  'companion-waiver': 'the Tenant Waiver & Acknowledgment',
  'mobility-aid': 'the mobility aid',
  'visions-answers': 'the VISIONS answers',
  'tech-agreement': 'the Tech Support agreement',
};

/** "… is not onboarded for Tech Support yet. Still missing: the Tech Support agreement. …" */
export function onboardingRefusalEn(name: string, o: Onboarding): string {
  const list = o.missing.map((p) => PIECE_EN[p]);
  const what = list.length > 1 ? `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}` : (list[0] ?? '');
  const fix = o.missing.length === 1 && o.missing[0] === 'tech-agreement' ? 'Complete it for them or send it by email, then book.' : 'Finish it on their profile (People), then book.';
  return `${name} is not onboarded for ${PROGRAM_NAME_EN[o.program]} yet. Still missing: ${what}. ${fix}`;
}

/* ------------------------------------------------------------------------------------------------
 * The volunteer request and the Companion / Reading booking: which gate refuses it
 * ---------------------------------------------------------------------------------------------- */

/** What the request's gate needs of the tenant (shared/volunteers-api.ts TenantGateView has it all). */
export interface RequestGateFacts {
  companion: { active: boolean; waiver: boolean; emergency?: boolean; mobility: string | null };
  reading: { active: boolean };
  /** 1.6.7 (absent on an answer from before it: the rules from before then decide alone). */
  onboarding?: { companion: Onboarding; reading: Onboarding } | null;
}

export type RequestGate =
  | { why: 'waiver' | 'mobility' | 'active' }
  /** 1.6.7: a new person who is not onboarded — the pieces are in `onboarding.missing`. */
  | { why: 'onboarding'; onboarding: Onboarding };

/**
 * The gate on a request, in order. The rules from before 1.6.7 hold for everyone as they were (the waiver, the
 * emergency contact, the mobility aid, the switch Active; Reading & Services turned on). What 1.6.7 adds — the
 * rest of the onboarding (contact details, the VISIONS answers) — is asked of a new person only: an existing
 * tenant of the program is `complete` and is never stopped by it.
 */
export function requestGate(g: RequestGateFacts, companionGated: boolean): RequestGate | null {
  if (companionGated) {
    const o = g.onboarding?.companion ?? null;
    if (!g.companion.waiver) return { why: 'waiver' };
    if (!g.companion.mobility) return { why: 'mobility' };
    // the switch alone not on: the words from before say it (below)
    if (o && !o.complete && o.missing.some((p) => p !== 'enrolled')) return { why: 'onboarding', onboarding: o };
    if (!g.companion.active) return { why: 'active' };
    return null;
  }
  // 1.8.3 (D-1.8.3-4 — Elijah, 9 Oct 2026: "for reading we don't have anything for tenants to sign off on"):
  // Reading & Services is open to every tenant — nothing refuses it. The booking writes the enrollment it needs.
  return null;
}
